// ./src/components/features/EventTickets/TicketRemovalDrawer/TicketRemovalDrawer.tsx
//
// ROADMAP #187 — removing tickets (the Box Office) or guests holding them (the guest list), and what that means. Owner,
// 2026-10-20: "if we remove guests who have tickets, then we need to void tickets so availability counts comes back …
// if all tickets of transaction were removed then refund the whole transaction(s)". The removal used to delete the rows
// and nothing else: the places stayed taken, every order behind them still read "paid", nobody was refunded.
//
// ⚠ IT SHOWS THE CONSEQUENCES BEFORE ANYTHING HAPPENS — read fresh from the server each time it opens
//   (`ticketsRemovalPlan`): how many places come back, who stays on the list marked not coming, which orders are refunded
//   WHOLE and how much money is involved. A failed read is an error, never "nothing to refund".
// ⚠ PAID TICKETS NEED A STATED CHOICE — "Refund" (the default) or "Don't refund"; the server refuses the removal without
//   one, and refuses either choice to someone who does not manage tickets (offered here only when the plan says they may).
// ⚠ A long selection is several calls (the server refunds a few orders per call and says how many are left); the drawer
//   repeats the same call until nothing is left, showing the progress, and stops at the first refusal with the reason.
// ⓘ A drawer, not ConfirmModal: it carries a choice and a read (house rule #115). No ⌘S — a refund cannot be undone.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Group, Input, Loader, SegmentedControl, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { OpResult } from "@sdflc/api-helpers";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { ErrorAlert } from "@/components/generic/ErrorAlert";
import { EventTicketService, TICKET_REMOVAL_REFUND } from "@/utils/api";
import type { TicketRemovalRefund, TicketsRemovalPlan, TicketsRemoved } from "@/utils/api";
import { formatCurrency, getFormErrors, hasAnyFieldError } from "@/utils";
import { mergeRemovalPlans, removalInputs, runTicketRemoval } from "@/utils/ticketRemoval";
import { useFormSnapshot } from "@/hooks";

/**
 * The server names a refusal about the money choice `refund` ("choose whether to refund them", "only someone who manages
 * tickets…"): it lands on the choice — or, when the choice is not on screen, on its own line, so it is never swallowed.
 */
const KNOWN_FORM_FIELDS = ["refund"] as const;

/** Orders listed by name before "and N more". */
const ORDERS_SHOWN = 5;

export interface TicketRemovalDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** Box Office: the tickets to remove. */
  ticketIds?: string[];
  /** Guest list: the guests to remove — their tickets go with them. */
  eventGuestIds?: string[];
  title: string;
  /** What is being removed, as a question ("Remove ticket ABC123?", "Remove Jane Doe?"). */
  question: string;
  /** After a removal ran — all of it, or part of it before a refusal — so the page re-reads its list. */
  onRemoved: (outcome: TicketsRemoved) => void;
}

/** The shared currency format ("CA$ 63.00"), in the event's currency. */
const money = (amount?: number | null, currencyCode?: string | null): string => formatCurrency(Number(amount), currencyCode || "USD");

const TicketRemovalDrawerComponent: React.FC<TicketRemovalDrawerProps> = ({ opened, onClose, eventId, ticketIds, eventGuestIds, title, question, onRemoved }) => {
  const { t } = useTranslation(["ticketSales", "common"]);
  const [plan, setPlan] = useState<TicketsRemovalPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);
  const [choice, setChoice] = useState<TicketRemovalRefund>(TICKET_REMOVAL_REFUND.REFUND);
  const [removing, setRemoving] = useState(false);
  const [refundsDone, setRefundsDone] = useState(0);
  const [removeError, setRemoveError] = useState<OpResult | null>(null);

  // The refund choice is the one thing set here: Esc / a click outside ask before dropping a changed one (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot({ choice });

  // Only the latest plan read may land: one still in flight from an earlier open is dropped (#187 review).
  const planRequest = useRef(0);
  const ticketKey = (ticketIds ?? []).join(",");
  const guestKey = (eventGuestIds ?? []).join(",");

  /** ⚠ Read EVERY time it opens, never cached: the money figure is what the host is about to decide on. */
  const loadPlan = useCallback(async (): Promise<void> => {
    const request = ++planRequest.current;
    setPlanLoading(true);
    setLoadError(null);

    const parts: TicketsRemovalPlan[] = [];
    for (const input of removalInputs(eventId, ticketKey ? ticketKey.split(",") : [], guestKey ? guestKey.split(",") : [], null)) {
      const result = await EventTicketService.removalPlan(input);
      if (request !== planRequest.current) {
        return; // a newer read (or a close) took over, and owns the loading flag
      }

      if (!result.didSucceed()) {
        // A failed read is NOT "nothing to refund": the figures are suppressed and the error shown.
        setPlan(null);
        setLoadError(result);
        setPlanLoading(false);

        return;
      }

      parts.push(result.getDataFirst() as TicketsRemovalPlan);
    }

    setPlan(mergeRemovalPlans(parts));
    setPlanLoading(false);
  }, [eventId, ticketKey, guestKey]);

  // The choice opens on Refund — seeded on OPEN only, never again while it is open (a re-read is not a new removal).
  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    setChoice(TICKET_REMOVAL_REFUND.REFUND);
    seedFrom({ choice: TICKET_REMOVAL_REFUND.REFUND });
    setRemoveError(null);
    setRefundsDone(0);
  }, [opened, seedFrom, clear]);

  useEffect(() => {
    if (!opened) {
      planRequest.current += 1;
      setPlanLoading(false);

      return;
    }

    void loadPlan();
  }, [opened, loadPlan]);

  const paid = plan?.needsRefundChoice === true;
  const refunding = paid && choice === TICKET_REMOVAL_REFUND.REFUND;
  const refundOrders = useMemo(() => (plan?.orders ?? []).filter((o) => Number(o.refundableAmount) > 0), [plan]);
  const blocked = !!plan?.blockedMessage;
  const mayNotDecide = paid && plan?.mayDecideRefund !== true;
  const showsChoice = paid && !blocked && !mayNotDecide;
  const { fields: fieldErrors, genericErrors } = useMemo(() => getFormErrors(removeError, KNOWN_FORM_FIELDS), [removeError]);

  const handleConfirm = useCallback(async () => {
    setRemoving(true);
    setRemoveError(null);
    setRefundsDone(0);

    const inputs = removalInputs(eventId, ticketKey ? ticketKey.split(",") : [], guestKey ? guestKey.split(",") : [], paid ? choice : null);
    const { outcome, refused } = await runTicketRemoval(inputs, (input) => EventTicketService.removeTickets(input), (soFar) => setRefundsDone(soFar.refundedOrderIds.length));

    setRemoving(false);

    if (refused) {
      // ⚠ Part of it may have happened (an earlier order refunded and removed): the page re-reads, the drawer stays open
      // on the reason and re-reads what is left.
      setRemoveError(refused);
      if (outcome.removedTicketIds.length + outcome.removedGuestIds.length + outcome.refundedOrderIds.length > 0) {
        onRemoved(outcome);
        void loadPlan();
      }

      return;
    }

    onRemoved(outcome);
    onClose();
  }, [eventId, ticketKey, guestKey, paid, choice, onRemoved, onClose, loadPlan]);

  const methodLabel = (processor: string | null): string =>
    processor === "stripe"
      ? t("ticketSales:remove.byCard", { defaultValue: "card" })
      : t("ticketSales:remove.byHand", { defaultValue: "paid in person" });

  const confirmLabel = refunding
    ? t("ticketSales:remove.confirmRefund", { defaultValue: "Refund and remove" })
    : t("ticketSales:remove.confirm", { defaultValue: "Remove" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      mobileFitContent
      closable={!removing}
      actionType="confirm-cancel"
      onConfirm={handleConfirm}
      confirmLabel={confirmLabel}
      confirmIcon={refunding ? "Coin" : "Trash"}
      confirmColor="red"
      confirmDisabled={planLoading || !plan || blocked || mayNotDecide}
      loading={removing}
      errors={genericErrors.length > 0 ? genericErrors : null}
      hasFieldErrors={hasAnyFieldError(fieldErrors)}
      cancelLabel={t("common:buttons.cancel", { defaultValue: "Cancel" })}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <Text size="md">{question}</Text>

        {planLoading && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="md" c="dimmed">
              {t("ticketSales:remove.checking", { defaultValue: "Checking the tickets and payments…" })}
            </Text>
          </Group>
        )}

        {loadError && <ErrorAlert error={loadError} title={t("ticketSales:remove.planFailed", { defaultValue: "We could not check the tickets" })} page="Remove tickets" operation="ticketsRemovalPlan" />}

        {plan && !planLoading && (
          <Stack gap="sm">
            {plan.ticketCount > 0 && (
              <Text size="md">
                {plan.placeCount > 0
                  ? t("ticketSales:remove.places", {
                      defaultValue: "{{count}} ticket(s) are cancelled and their places go back on sale.",
                      count: plan.placeCount,
                    })
                  : t("ticketSales:remove.hidden", { defaultValue: "The ticket(s) are already void or refunded — they are only removed from the list." })}
              </Text>
            )}

            {plan.holderGuestCount > 0 && (
              <Text size="md">
                {t("ticketSales:remove.holders", {
                  defaultValue: "{{count}} guest(s) holding them stay on the guest list, marked as not coming.",
                  count: plan.holderGuestCount,
                })}
              </Text>
            )}

            {plan.otherHolderNames.length > 0 && (
              <Text size="md">
                {t("ticketSales:remove.otherHolders", {
                  defaultValue: "This includes the tickets bought for {{names}}.",
                  names: plan.otherHolderNames.join(", "),
                })}
              </Text>
            )}

            {blocked && (
              <Text size="md" c="red">
                {plan.blockedMessage}
              </Text>
            )}

            {paid && !blocked && mayNotDecide && (
              <Text size="md" c="red">
                {t("ticketSales:remove.cannotDecide", {
                  defaultValue: "These tickets were paid for. Only someone who manages tickets can remove them, and decide about the refund.",
                })}
              </Text>
            )}

            {fieldErrors.refund && !showsChoice && (
              <Text size="md" c="red">
                {fieldErrors.refund}
              </Text>
            )}

            {showsChoice && (
              <>
                <Input.Wrapper
                  label={t("ticketSales:remove.moneyLabel", { defaultValue: "{{amount}} was paid", amount: money(plan.refundableAmount, plan.currencyCode) })}
                  size="md"
                  error={fieldErrors.refund}
                >
                  <Box>
                    <SegmentedControl
                      fullWidth
                      size="md"
                      value={choice}
                      onChange={(v) => setChoice(v as TicketRemovalRefund)}
                      disabled={removing}
                      data={[
                        { label: t("ticketSales:remove.refund", { defaultValue: "Refund" }), value: TICKET_REMOVAL_REFUND.REFUND },
                        { label: t("ticketSales:remove.keep", { defaultValue: "Don't refund" }), value: TICKET_REMOVAL_REFUND.KEEP },
                      ]}
                    />
                  </Box>
                </Input.Wrapper>

                <Text size="md" c="dimmed">
                  {refunding
                    ? t("ticketSales:remove.refundHint", {
                        defaultValue:
                          "An order whose every ticket is removed is refunded in full, fees included. Card payments go back to the card; anything paid in person is recorded as refunded — hand the money back.",
                      })
                    : t("ticketSales:remove.keepHint", { defaultValue: "The money stays with you, and the payments still read as paid." })}
                </Text>

                <Stack gap={4}>
                  {refundOrders.slice(0, ORDERS_SHOWN).map((o) => (
                    <Group key={o.orderId} justify="space-between" wrap="nowrap" gap="sm">
                      <Text size="md" truncate>
                        {[o.buyerName, o.referenceCode].filter(Boolean).join(" · ") || t("ticketSales:remove.anOrder", { defaultValue: "An order" })}
                        {" — "}
                        {o.wholeOrder
                          ? t("ticketSales:remove.wholeOrder", { defaultValue: "the whole order" })
                          : t("ticketSales:remove.someTickets", { defaultValue: "{{count}} ticket(s)", count: o.ticketCount })}
                        {", "}
                        {methodLabel(o.processor)}
                        {o.isTest ? ` (${t("ticketSales:remove.testMode", { defaultValue: "test mode" })})` : ""}
                      </Text>
                      <Text size="md" fw={600} style={{ flexShrink: 0 }}>
                        {money(o.refundableAmount, o.currencyCode)}
                      </Text>
                    </Group>
                  ))}
                  {refundOrders.length > ORDERS_SHOWN && (
                    <Text size="md" c="dimmed">
                      {t("ticketSales:remove.moreOrders", { defaultValue: "and {{count}} more order(s)", count: refundOrders.length - ORDERS_SHOWN })}
                    </Text>
                  )}
                </Stack>
              </>
            )}

            {removing && refunding && refundOrders.length > 1 && (
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="md" c="dimmed">
                  {t("ticketSales:remove.progress", { defaultValue: "Refunding… {{done}} of {{total}} orders", done: refundsDone, total: refundOrders.length })}
                </Text>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </AppDrawer>
  );
};

export const TicketRemovalDrawer = React.memo(TicketRemovalDrawerComponent);
